import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { readingTime } from "./format";
import { accuracyText, coordinate, POOR_ACCURACY_M, geocodeStale, parsePoint, pointWords, restoredText, ROUGH_PIN_CLASS, samePoint, SOURCE_TEXT, tankUndo, doorUndo, zoomToShow } from "./location";
import { dotTitle, levelDot } from "./fill";
import { LevelDot } from "./LevelDot";
import { type MapInfo, MapInfoBox } from "./MapInfoBox";
import { MapFullscreenButton } from "./MapFullscreen";
import { type GoogleMap, loadMaps, type Maps, type Marker, pointOf, watchMapErrors } from "./maps";
import { PinHistory } from "./PinHistory";
import { type Saved, saveFailure } from "./save";
import { personName } from "./requests";
import { Skeleton } from "./Skeleton";

type Point = "door" | "tank";
type Field = "lat" | "lng";
type MapType = "roadmap" | "hybrid";

interface Draft {
  lat: string;
  lng: string;
  source: "manual" | "gps";
  accuracy: number | null;
}

const PINS: Record<Point, { glyph: string; background: string }> = {
  door: { glyph: "D", background: "#5aa2f0" },
  tank: { glyph: "T", background: "#ffc98a" },
};
const NO_POINT = { lat: 39.8, lng: -98.6 };
const POINTS: Point[] = ["door", "tank"];
const DIMMED = "0.45";

const draftOf = (point: api.LatLng | null | undefined): Draft => ({
  lat: point ? coordinate(point.lat) : "",
  lng: point ? coordinate(point.lng) : "",
  source: "manual",
  accuracy: null,
});

function draftPoint(draft: Draft): api.LatLng | null {
  const parsed = parsePoint(draft.lat, draft.lng);
  return "error" in parsed ? null : parsed;
}

function PointFacts({ point, timeZone, testId, empty }: { point: api.MapPoint | null | undefined; timeZone: string; testId: string; empty: string }) {
  if (!point) return <dd data-testid={testId}>{empty}</dd>;
  const accuracy = point.source === "gps" ? accuracyText(point.accuracy_m) : "";
  const stamp = [point.set_by ? `by ${personName(point.set_by)}` : "", point.set_at ? readingTime(point.set_at, timeZone) : ""].filter(Boolean).join(", ");
  return (
    <dd data-testid={testId}>
      {coordinate(point.lat)}, {coordinate(point.lng)}
      <span className="sub location-stamp">
        {SOURCE_TEXT[point.source]}
        {accuracy && ` ${accuracy}`}
        {stamp && ` · set ${stamp}`}
      </span>
      {geocodeStale(point, new Date()) && <span className="chip-flag">Refresh due</span>}
      {point.check && (
        <span className={ROUGH_PIN_CLASS} data-testid="location-check">
          Rough geocode: check the pin
        </span>
      )}
    </dd>
  );
}

/** The key mark of a point: the door pin, or the container as a level dot or a pin. */
function PointKey({ which, dotted, fill }: { which: Point; dotted: boolean; fill?: number | null }) {
  if (which === "tank" && dotted) return <LevelDot share={fill ?? null} small />;
  return (
    <span className={`location-glyph ${which}`} aria-hidden="true">
      <span>{PINS[which].glyph}</span>
    </span>
  );
}

/** Spec 5.7: the front door and the tank pin on a Google map, with a form to move one of them. */
export function LocationCard({
  accountId,
  door,
  tank,
  hasTank,
  canDoor,
  canTank,
  address,
  timeZone,
  user,
  save,
  doorName = "Front door",
  doorWord = "door",
  title = "Location",
  fill,
  fillName,
}: {
  accountId?: string;
  door: api.MapPoint | null | undefined;
  tank?: api.MapPoint | null;
  hasTank: boolean;
  canDoor: boolean;
  canTank: boolean;
  address: string;
  timeZone: string;
  user: User;
  save: (change: api.LocationChange) => Promise<void>;
  /** The name of the one point when the card is not for a site, for example "Depot point". */
  doorName?: string;
  /** The point in the button words, for example "Change door location". */
  doorWord?: string;
  title?: string;
  /** The fill share of the container: the container pin is then a level dot (rule U-22). */
  fill?: number | null;
  fillName?: string;
}) {
  const pointNames: Record<Point, string> = { door: doorName, tank: "Container pin" };
  const doorText = doorName.toLowerCase();
  const words: Record<Point, ReturnType<typeof pointWords>> = { door: pointWords(doorWord), tank: pointWords("container") };
  const editable = POINTS.filter((which) => (which === "door" ? canDoor : hasTank && canTank));
  const element = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMap | null>(null);
  const markers = useRef<Partial<Record<Point, Marker>>>({});
  const statusRef = useRef<HTMLParagraphElement>(null);
  const editingRef = useRef<HTMLParagraphElement>(null);
  const editButtons = { door: useRef<HTMLButtonElement>(null), tank: useRef<HTMLButtonElement>(null) };
  const [maps, setMaps] = useState<Maps | null>(null);
  const [mapState, setMapState] = useState<"loading" | "ready" | "failed">("loading");
  const [mapType, setMapType] = useState<MapType>("hybrid");
  const [editing, setEditing] = useState(false);
  const [point, setPoint] = useState<Point>(editable.includes("tank") ? "tank" : "door");
  const [drafts, setDrafts] = useState<Record<Point, Draft>>({ door: draftOf(door), tank: draftOf(tank) });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState<Saved | null>(null);
  const [undo, setUndo] = useState<api.LocationChange | null>(null);
  const [problem, setProblem] = useState<{ field: Field; text: string } | null>(null);
  const fields = { lat: useRef<HTMLInputElement>(null), lng: useRef<HTMLInputElement>(null) };
  const [seen, setSeen] = useState(false);
  const [open, setOpen] = useState<MapInfo | null>(null);
  const opener = useRef<Marker | null>(null);
  const dotted = fill !== undefined;

  useEffect(() => {
    const target = element.current;
    if (!target || seen) return;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && setSeen(true), { rootMargin: "200px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [seen]);

  // Each new map counts against the daily Maps quota, so the map loads only when it shows.
  useEffect(() => {
    if (!seen) return;
    let live = true;
    user
      .getIdToken()
      .then(loadMaps)
      .then((loaded) => {
        if (live) setMaps(loaded);
      })
      .catch(() => live && setMapState("failed"));
    return () => {
      live = false;
    };
  }, [user, seen]);

  useEffect(() => {
    if (!editing) setDrafts({ door: draftOf(door), tank: draftOf(tank) });
  }, [door, tank, editing]);

  const stored: Record<Point, api.LatLng | null> = { door: door ?? null, tank: hasTank ? (tank ?? null) : null };
  const shown: Record<Point, api.LatLng | null> = editing
    ? { door: draftPoint(drafts.door) ?? stored.door, tank: hasTank ? (draftPoint(drafts.tank) ?? stored.tank) : null }
    : stored;
  const shownKey = JSON.stringify(shown);

  // The map is made once; later changes move the markers only.
  useEffect(() => {
    if (!maps || !element.current || map.current) return;
    const first = stored.tank ?? stored.door;
    try {
      map.current = new maps.maps.Map(element.current, {
        center: first ?? NO_POINT,
        zoom: first ? 18 : 4,
        mapId: maps.mapId,
        mapTypeId: mapType,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      setMapState("ready");
    } catch {
      map.current = null;
      setMapState("failed");
    }
  }, [maps]);

  // At rest, and again after a save, an undo or a cancel, the map shows both points.
  const storedKey = JSON.stringify(stored);
  useEffect(() => {
    const view = map.current;
    if (!maps || !view || mapState !== "ready" || editing) return;
    if (stored.door && stored.tank && !samePoint(stored.door, stored.tank)) {
      const bounds = new maps.maps.LatLngBounds();
      bounds.extend(stored.door);
      bounds.extend(stored.tank);
      view.fitBounds(bounds, 48);
    } else if (stored.tank ?? stored.door) {
      view.setCenter((stored.tank ?? stored.door) as api.LatLng);
      view.setZoom(18);
    }
  }, [maps, mapState, editing, storedKey]);

  useEffect(() => {
    const target = element.current;
    if (mapState !== "ready" || !target) return;
    return watchMapErrors(target, () => setMapState("failed"));
  }, [mapState]);

  useEffect(() => {
    map.current?.setMapTypeId(mapType);
  }, [mapType]);

  useEffect(() => {
    const view = map.current;
    if (!maps || !view) return;
    for (const which of POINTS) {
      let marker = markers.current[which];
      if (!marker) {
        const pin = new maps.marker.PinElement({ glyph: PINS[which].glyph, background: PINS[which].background, borderColor: "#0f1216", glyphColor: "#0f1216" });
        const clickable = which === "tank" && dotted;
        const made = new maps.marker.AdvancedMarkerElement({ title: pointNames[which], content: pin.element, gmpClickable: clickable });
        if (clickable) {
          made.addListener("click", () => {
            opener.current = made;
            setOpen({ id: "tank", title: pointNames.tank, name: fillName ?? pointNames.tank, share: fill });
          });
        }
        markers.current[which] = made;
        marker = made;
      }
      if (which === "tank" && dotted) {
        marker.content = levelDot({ share: fill ?? null });
        marker.title = dotTitle([pointNames.tank, fillName], fill);
      }
      const at = editing && which === point ? null : shown[which];
      const content = marker.content as HTMLElement | null;
      if (content?.style) content.style.opacity = editing ? DIMMED : "";
      marker.position = at;
      marker.map = at ? view : null;
      marker.gmpDraggable = false;
    }
    // The walk and the UI review read the count of the shown markers.
    element.current?.setAttribute("data-markers", String(POINTS.filter((which) => markers.current[which]?.map).length));
  }, [maps, shownKey, editing, point, canDoor, canTank, hasTank, fill]);

  // The dimmed other point stays in the view as a reference; the edited point stays in the centre.
  const showOther = (at: api.LatLng | null) => {
    const other = stored[point === "door" ? "tank" : "door"];
    const view = map.current;
    const box = element.current;
    if (!view || !box || !at || !other || samePoint(at, other)) return;
    const fit = zoomToShow(at, other, box.clientWidth, box.clientHeight);
    if (fit < (view.getZoom() ?? 0)) view.setZoom(fit);
  };
  // While editing, the point being changed is the center of the map, under the dot.
  const aim = (at: api.LatLng | null) => {
    const view = map.current;
    const now = view?.getCenter();
    if (!view || !at || (now && samePoint(pointOf(now), at))) return;
    view.setCenter(at);
    if ((view.getZoom() ?? 0) < 18) view.setZoom(19);
    showOther(at);
  };
  useEffect(() => {
    if (!editing) return;
    const at = draftPoint(drafts[point]) ?? stored[point] ?? stored.door ?? stored.tank;
    aim(at);
    showOther(at);
    if (at && !draftPoint(drafts[point])) change(point, draftOf(at));
  }, [mapState, editing, point]);
  const typed = editing ? draftPoint(drafts[point]) : null;
  const typedKey = JSON.stringify(typed);
  useEffect(() => aim(typed), [typedKey]);
  useEffect(() => {
    const view = map.current;
    if (!view || !editing) return;
    const moved = view.addListener("idle", () => {
      const now = view.getCenter();
      if (!now) return;
      const at = pointOf(now);
      setDrafts((all) => {
        const current = all[point];
        const was = draftPoint(current);
        const halfTyped = !was && (current.lat.trim() || current.lng.trim());
        if (halfTyped || (was && samePoint(was, at))) return all;
        return { ...all, [point]: { ...draftOf(at), source: "manual" } };
      });
    });
    return () => moved.remove();
  }, [mapState, editing, point]);

  useEffect(
    () => () => {
      for (const marker of Object.values(markers.current)) if (marker) marker.map = null;
    },
    [],
  );

  const run = async (work: () => Promise<void>, done: string, offerUndo: api.LocationChange | null = null) => {
    setSaving(true);
    setStatus(null);
    setUndo(null);
    setProblem(null);
    try {
      await work();
      setEditing(false);
      setStatus({ ok: true, text: done });
      setUndo(offerUndo);
    } catch (e) {
      setStatus(saveFailure(e, "The location"));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  };
  const change = (which: Point, values: Partial<Draft>) => {
    setProblem(null);
    setDrafts((all) => ({ ...all, [which]: { ...all[which], ...values } }));
  };
  const startEdit = (which: Point) => {
    setDrafts({ door: draftOf(door), tank: draftOf(tank) });
    setPoint(which);
    setStatus(null);
    setUndo(null);
    setProblem(null);
    setEditing(true);
    requestAnimationFrame(() => editingRef.current?.focus());
  };
  const cancel = () => {
    setEditing(false);
    setStatus(null);
    setProblem(null);
    requestAnimationFrame(() => editButtons[point].current?.focus());
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const draft = drafts[point];
    const parsed = parsePoint(draft.lat, draft.lng);
    if ("error" in parsed) {
      setStatus(null);
      setProblem({ field: parsed.field, text: `${pointNames[point]}: ${parsed.error}` });
      requestAnimationFrame(() => fields[parsed.field].current?.focus());
      return;
    }
    if (samePoint(parsed, stored[point]) && draft.source === "manual") {
      cancel();
      setStatus({ ok: true, text: "Nothing changed." });
      return;
    }
    const gps = point === "tank" && draft.source === "gps";
    const item: api.LocationChange = { point, ...parsed, source: gps ? "gps" : "manual", ...(gps ? { accuracy_m: draft.accuracy } : {}) };
    void run(() => save(item), words[point].saved, point === "tank" ? tankUndo(tank) : doorUndo(door));
  };
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setStatus({ ok: false, text: "This browser cannot give a GPS position." });
      return;
    }
    setLocating(true);
    setStatus(null);
    setUndo(null);
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        setLocating(false);
        if (!editing) setDrafts({ door: draftOf(door), tank: draftOf(tank) });
        setEditing(true);
        setPoint("tank");
        change("tank", { lat: coordinate(fix.coords.latitude), lng: coordinate(fix.coords.longitude), source: "gps", accuracy: fix.coords.accuracy });
        setStatus(null);
      },
      (error) => {
        setLocating(false);
        const denied = error.code === error.PERMISSION_DENIED;
        setStatus({ ok: false, text: denied ? "Allow the location for this site, then try again." : "No GPS position. Go outside and try again." });
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  };
  const draft = drafts[point];
  const canGps = hasTank && canTank;
  const fieldProps = (field: Field) => ({
    ref: fields[field],
    "aria-invalid": problem?.field === field || undefined,
    "aria-describedby": problem?.field === field ? "location-field-error" : undefined,
  });
  const fieldError = (field: Field) =>
    problem?.field === field && (
      <p id="location-field-error" role="alert" className="error" data-testid="location-field-error">
        {problem.text}
      </p>
    );

  const poor = draft.source === "gps" && (draft.accuracy ?? 0) > POOR_ACCURACY_M;
  const geocoded = canDoor && door?.source === "geocode" ? door : null;
  const aimDot = point === "tank" && dotted;
  const keyed = POINTS.filter((which) => stored[which] || (editing && which === point));
  const editButton = (which: Point) =>
    editable.includes(which) && (
      <button type="button" ref={editButtons[which]} data-testid={`location-edit-${which}`} disabled={saving} onClick={() => startEdit(which)}>
        {words[which].change}
      </button>
    );
  const gpsButton = canGps && (
    <button type="button" data-testid="location-gps" disabled={locating || saving} onClick={locate}>
      {locating ? "Finding your position…" : "Use my location"}
    </button>
  );
  const layers = (
    <div className="view-switch location-layers" role="group" aria-label="Map layer">
      {(["hybrid", "roadmap"] as const).map((type) => (
        <button key={type} type="button" aria-pressed={mapType === type} data-testid={`location-${type}`} onClick={() => setMapType(type)}>
          {type === "roadmap" ? "Map" : "Satellite"}
        </button>
      ))}
    </div>
  );

  return (
    <section aria-labelledby="location-title" className="location-card" data-testid="location-card">
      <h2 id="location-title">{title}</h2>
      {editing && (
        <p ref={editingRef} tabIndex={-1} className="location-editing" data-testid="location-editing" data-point={point}>
          <PointKey which={point} dotted={dotted} fill={fill} /> {words[point].editing}
        </p>
      )}
      <div ref={wrap} className="location-map-wrap">
        <div ref={element} className="location-map" data-testid="location-map" data-state={mapState} role="region" aria-label={hasTank ? "Map of the front door and the container pin" : `Map of the ${doorText}`} />
        {mapState === "ready" && <MapFullscreenButton wrap={wrap} testId="location-map-fullscreen" />}
        {editing && mapState === "ready" && (
          <div className={`location-aim ${point}${aimDot ? " dotted" : ""}`} data-testid="location-aim" data-point={point} aria-hidden="true">
            {aimDot ? <LevelDot share={fill ?? null} /> : <span>{PINS[point].glyph}</span>}
          </div>
        )}
        {mapState === "loading" && <Skeleton shape="map" overlay label="Loading the map" />}
        {mapState === "failed" && (
          <p className="location-map-note map-warning" data-testid="location-map-failed">
            The map cannot load. You can still type the latitude and the longitude.
          </p>
        )}
        {open && !editing && (
          <MapInfoBox
            testId="location-box"
            info={{ ...open, share: fill }}
            onClose={() => {
              setOpen(null);
              (opener.current as unknown as HTMLElement | null)?.focus?.();
            }}
          />
        )}
      </div>
      {hasTank && keyed.length > 0 && (
        <ul className="location-key" data-testid="location-key" aria-label="Map key">
          {keyed.map((which) => (
            <li key={which} data-point={which} className={editing && which !== point ? "dimmed" : undefined}>
              <PointKey which={which} dotted={dotted} fill={fill} /> {which === "door" ? doorName : "Container"}
              {editing && which === point && <span className="sub"> (editing)</span>}
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <form className="lead-form location-form" data-testid="location-form" onSubmit={submit} noValidate>
          <p className="sub location-hint">
            Move the map until the marker in the middle is on the {point === "tank" ? "container" : doorText}, then save.
          </p>
          {point === "tank" && draft.source === "gps" && (
            <p className={poor ? "location-poor" : "sub"} data-testid="location-accuracy">
              {poor
                ? `The GPS position is only good to ±${Math.round(draft.accuracy ?? 0)} m. Move the map to put the marker on the container.`
                : `From the phone GPS, ${accuracyText(draft.accuracy)}.`}
            </p>
          )}
          <div className="actions location-actions">
            <button className="primary" data-testid="location-save" disabled={saving}>
              {saving ? "Saving…" : words[point].save}
            </button>
            {point === "tank" && gpsButton}
            <button type="button" data-testid="location-cancel" onClick={cancel}>
              Cancel
            </button>
            {mapState !== "failed" && layers}
          </div>
          <p ref={statusRef} tabIndex={-1} role="status" data-testid="location-status" className={status && !status.ok ? "error" : undefined}>
            {status?.text}
          </p>
          <div className="location-typed">
            <div>
              <label htmlFor="location-lat">Latitude</label>
              <input
                id="location-lat"
                data-testid="location-lat"
                inputMode="decimal"
                autoComplete="off"
                value={draft.lat}
                {...fieldProps("lat")}
                onChange={(e) => change(point, { lat: e.target.value, source: "manual", accuracy: null })}
              />
            </div>
            <div>
              <label htmlFor="location-lng">Longitude</label>
              <input
                id="location-lng"
                data-testid="location-lng"
                inputMode="decimal"
                autoComplete="off"
                value={draft.lng}
                {...fieldProps("lng")}
                onChange={(e) => change(point, { lng: e.target.value, source: "manual", accuracy: null })}
              />
            </div>
          </div>
          {fieldError("lat")}
          {fieldError("lng")}
        </form>
      ) : (
        <>
          <div className="actions location-actions">
            {gpsButton}
            {editButton("tank")}
            {editButton("door")}
            {mapState !== "failed" && layers}
          </div>
          <p ref={statusRef} tabIndex={-1} role="status" data-testid="location-status" className={status && !status.ok ? "error" : undefined}>
            {status?.text}
            {status?.ok && undo && (
              <>
                {" "}
                <button
                  type="button"
                  data-testid="location-undo"
                  disabled={saving}
                  onClick={() => void run(() => save(undo), undo.point === "tank" ? "The container pin is back as it was." : `The ${doorText} is back as it was.`)}
                >
                  Undo
                </button>
              </>
            )}
          </p>
        </>
      )}
      <dl className="facts location-facts">
        <dt>{doorName}</dt>
        <PointFacts point={door} timeZone={timeZone} testId="location-door-text" empty={address ? "Not set. Google found no point for the address yet." : "Not set."} />
        {hasTank && (
          <>
            <dt>Container pin</dt>
            <PointFacts point={tank} timeZone={timeZone} testId="location-tank-text" empty="Not set. Drivers go to the front door." />
          </>
        )}
      </dl>
      {!editing && editable.length > 0 && (
        <div className="actions">
          {canDoor && address.trim() && (
            <button
              type="button"
              data-testid="location-reset-door"
              disabled={saving}
              onClick={() => void run(() => save({ point: "door", reset: true }), `Saved: the ${doorText} is at the geocoded address again.`)}
            >
              Reset {doorWord} to geocoded
            </button>
          )}
          {geocoded && (
            <button
              type="button"
              data-testid="location-confirm-door"
              disabled={saving}
              onClick={() =>
                void run(
                  () => save({ point: "door", lat: geocoded.lat, lng: geocoded.lng, source: "manual" }),
                  `Saved: the ${doorText} is checked. The night job keeps it as a point placed by hand.`,
                )
              }
            >
              The {doorWord} pin is right
            </button>
          )}
          {canGps && tank && (
            <button
              type="button"
              data-testid="location-clear-tank"
              disabled={saving}
              onClick={() =>
                void run(() => save({ point: "tank", reset: true }), "Saved: the container pin is cleared. Drivers go to the front door.", tankUndo(tank))
              }
            >
              Clear the container pin
            </button>
          )}
        </div>
      )}
      {hasTank && accountId && (
        <PinHistory
          accountId={accountId}
          tank={tank}
          canRestore={canGps}
          disabled={saving || editing}
          timeZone={timeZone}
          user={user}
          onRestore={(change, entry) => void run(() => save(change), restoredText(entry, timeZone), tankUndo(tank))}
        />
      )}
    </section>
  );
}
